import {useEffect, useState} from 'react';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {Alert, Box, Chip, Fab, Skeleton, Stack, Typography} from '@mui/material';
import {Add, BlockRounded, CheckRounded, PriorityHighRounded} from '@mui/icons-material';
import moment from 'moment';
import './attendance-tracker.daily-care-log.screen.scss';
import './attendance-session-details.daily-care-log.screen.scss';
import {AttendanceVoidedNotice} from '../../../../components/attendance-tracker/attendance-voided-notice';
import {AttendanceManualNotice} from '../../../../components/attendance-tracker/attendance-manual-notice';
import {AttendanceRecordSummary} from '../../../../components/attendance-tracker/attendance-record-summary';
import {AttendanceRecordSource} from '../../../../backend/enums/attendance-record-source';
import {AttendanceRecordType} from '../../../../backend/enums/attendance-record-type';
import {
  AttendanceTrackerSession,
  AttendanceTrackerSessionPoint,
} from '../../../../backend/responses/attendance-tracker-session';
import {useAuth} from '../../../../contexts/auth/context';
import {useDailyCareLog} from '../../../../contexts/daily-care-log/context';
import {ScreenPaths} from '../../../../enums';
import {isTouchDevice} from '../../../../hooks/touch-device';
import {useServerNow} from '../../../../hooks/use-server-now';
import {translations} from '../../../../i18n/translations';
import {EntityAttendanceTrackerRecordsRepository} from '../../../../repositories/entity-attendance-tracker-records';
import {Pallete} from '../../../../theme/bluebird';
import {
  formatRecordDateTime,
  formatSessionDuration,
  getSessionDurationSeconds,
  getSessionState,
} from '../../../../utils/functions/attendance-sessions';

type MovementColumnProps = {
  type: AttendanceRecordType;
  point?: AttendanceTrackerSessionPoint;
};

/** One side of the session: the entry or the exit, with its data and the map of its location */
const MovementColumn = ({type, point}: MovementColumnProps) => {
  const {t} = useTranslation();
  const {state} = useAuth();
  const texts = translations.daily_care_log.attendance_tracker;

  const dateTime = point ? formatRecordDateTime(point.registeredAt, point.timeZone) : undefined;

  return (
    <Box className="attendance-session-column">
      <AttendanceRecordSummary
        type={type}
        user={state.session?.currentUser}
        entityName={state.session?.selectedAffiliation?.entity.name}
        dateTime={dateTime?.text}
        timeZone={dateTime?.timeZone}
        location={point?.location}
        // Fixed on touch screens, so the map does not get in the way when scrolling the page
        interactiveMap={!isTouchDevice()}
        locationFallback={
          point?.source === AttendanceRecordSource.Manual ? t(texts.manual.no_location) : undefined
        }
        emptyMessage={point ? undefined : t(texts.details.no_exit)}
      >
        <AttendanceManualNotice point={point} />
      </AttendanceRecordSummary>
    </Box>
  );
};

export default function AttendanceSessionDetailsDailyCareLogScreen() {
  const {t} = useTranslation();
  const history = useHistory();
  const location = useLocation<{session?: AttendanceTrackerSession} | undefined>();
  const {entityId, sessionId}: {entityId: string; sessionId: string} = useParams();
  const dailyCareLogContext = useDailyCareLog();
  const texts = translations.daily_care_log.attendance_tracker;

  const searchParams = new URLSearchParams(location.search);
  const date = searchParams.get('date');

  // The session of the list opens right away, and it is refreshed from the backend (it is also what opens it
  // from a direct link)
  const [session, setSession] = useState<AttendanceTrackerSession | undefined>(
    location.state?.session?.id === sessionId ? location.state.session : undefined
  );
  const [serverTime, setServerTime] = useState<string | undefined>(undefined);
  const [hasError, setHasError] = useState(false);
  const now = useServerNow(serverTime);

  useEffect(() => {
    dailyCareLogContext.setNavigationHeader(true, t(translations.daily_care_log.generics.back));
    dailyCareLogContext.toggleBottomNavigation(false);

    return () => {
      dailyCareLogContext.setNavigationHeader(false);
      dailyCareLogContext.toggleBottomNavigation(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let isCancelled = false;
    setHasError(false);

    EntityAttendanceTrackerRecordsRepository.instance
      .getMySession(entityId, sessionId)
      .then((details) => {
        if (!isCancelled) {
          setSession(details.session);
          setServerTime(details.serverTime);
        }
      })
      .catch((e) => {
        console.error(e);
        if (!isCancelled) setHasError(true);
      });

    return () => {
      isCancelled = true;
    };
  }, [entityId, sessionId]);

  const state = session ? getSessionState(session, now) : 'closed';
  const isOverdue = state === 'overdue';
  // A voided session is not open: it does not count anymore
  const isOpen = state === 'running' || state === 'overdue';
  const duration = session
    ? formatSessionDuration(getSessionDurationSeconds(session, now))
    : undefined;

  return (
    <Box className="attendance-session-details">
      {hasError && !session ? (
        <Alert severity="error">{t(texts.load_error)}</Alert>
      ) : !session ? (
        <Stack gap="16px">
          <Skeleton variant="rounded" height={48} />
          <Skeleton variant="rounded" height={320} />
        </Stack>
      ) : (
        <Stack gap="20px">
          {/* State and duration of the session */}
          <Box className="attendance-session-summary">
            <Chip
              icon={
                state === 'closed' ? (
                  <CheckRounded />
                ) : state === 'voided' ? (
                  <BlockRounded />
                ) : state === 'overdue' ? (
                  <PriorityHighRounded />
                ) : undefined
              }
              label={t(
                state === 'running'
                  ? texts.session.running
                  : state === 'overdue'
                    ? texts.session.overdue
                    : state === 'voided'
                      ? texts.voided.tag
                      : texts.session.closed
              )}
              sx={{
                backgroundColor:
                  state === 'closed'
                    ? Pallete.green.base
                    : state === 'overdue'
                      ? Pallete.red.base
                      : state === 'voided'
                        ? Pallete.gray.light
                        : Pallete.blue.base,
                color: '#fff',
                '& .MuiChip-icon': {color: '#fff'},
              }}
            />
            {state !== 'voided' && (
              <Typography className="duration">
                {isOpen ? t(texts.session.elapsed, {duration}) : duration}
              </Typography>
            )}
          </Box>

          {/* Who voided it and why: it does not count anymore */}
          <AttendanceVoidedNotice voided={session.voided} />

          {isOverdue && (
            <Alert severity="error">
              {t(texts.session.overdue_alert, {
                date: moment(session.entry.registeredAt).format('DD/MM/YYYY HH:mm'),
              })}
            </Alert>
          )}

          {/* Entry on one side and exit on the other. Stacked on small screens. */}
          <Box className="attendance-session-columns" sx={{opacity: state === 'voided' ? 0.6 : 1}}>
            <MovementColumn type={AttendanceRecordType.Entry} point={session.entry} />
            <Box className="attendance-session-divider" />
            <MovementColumn type={AttendanceRecordType.Exit} point={session.exit} />
          </Box>

          {/* Add bottom space */}
          <Box sx={{minHeight: 'calc(86px + var(--attendance-bar-height, 0px) + 56px + 18px)'}} />
        </Stack>
      )}

      {isOpen && (
        <Fab
          color="primary"
          className="attendance-tracker-floating"
          size="large"
          variant="extended"
          onClick={() =>
            history.push(
              `${ScreenPaths.DailyCareLog}/${entityId}${ScreenPaths.DailyCareLog_NewAttendanceRecord}${date ? `?date=${date}` : ''}`
            )
          }
        >
          <Add />
          <Typography>{t(texts.register_exit)}</Typography>
        </Fab>
      )}
    </Box>
  );
}
